import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import { realtimeService, projectService, qaService } from '../services/api';
import { POLL_INTERVAL_FAST_MS, AUTO_REMOVE_COMPLETED_MS, STUCK_PROCESSING_TIMEOUT_MS } from '../constants/theme';

const PLAYWRIGHT_SERVICE_URL = import.meta.env.VITE_PLAYWRIGHT_SERVICE_URL || 'http://localhost:3001';

const QAProcessingContext = createContext(null);

// LocalStorage helpers for persisting process timestamps across page refresh
const QA_TIMESTAMPS_KEY = 'qa-process-timestamps';
const QA_PENDING_STARTS_KEY = 'qa-pending-starts';
const QA_STATE_VERSION_KEY = 'qa-state-version';
const CURRENT_STATE_VERSION = 2; // Bump this number to force-clear all stale processing state
const PENDING_START_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes safety cleanup
const MAX_TIMESTAMP_AGE_MS = 4 * 60 * 60 * 1000; // 4 hours — timestamps older than this are stale and get reset

// One-time reset: if version changed, nuke all stale processing state
if (localStorage.getItem(QA_STATE_VERSION_KEY) !== String(CURRENT_STATE_VERSION)) {
  localStorage.removeItem(QA_PENDING_STARTS_KEY);
  localStorage.removeItem(QA_TIMESTAMPS_KEY);
  localStorage.setItem(QA_STATE_VERSION_KEY, String(CURRENT_STATE_VERSION));
  console.info('[QAProcessing] State version bumped — cleared all stale processing data.');
}

// Startup cleanup: remove any timestamps older than MAX_TIMESTAMP_AGE_MS so stale
// elapsed timers (e.g. 141h from a run days ago) don't appear on next load.
(() => {
  try {
    const timestamps = JSON.parse(localStorage.getItem(QA_TIMESTAMPS_KEY) || '{}');
    const now = Date.now();
    let changed = false;
    Object.keys(timestamps).forEach((projectId) => {
      if ((now - timestamps[projectId]) > MAX_TIMESTAMP_AGE_MS) {
        delete timestamps[projectId];
        changed = true;
        console.info(`[QAProcessing] Cleared stale timestamp for project ${projectId} on startup.`);
      }
    });
    if (changed) localStorage.setItem(QA_TIMESTAMPS_KEY, JSON.stringify(timestamps));
  } catch { /* ignore */ }
})();

const getStoredTimestamps = () => {
  try {
    return JSON.parse(localStorage.getItem(QA_TIMESTAMPS_KEY) || '{}');
  } catch { return {}; }
};

const setStoredTimestamp = (projectId, timestamp) => {
  const timestamps = getStoredTimestamps();
  timestamps[projectId] = timestamp;
  localStorage.setItem(QA_TIMESTAMPS_KEY, JSON.stringify(timestamps));
};

const removeStoredTimestamp = (projectId) => {
  const timestamps = getStoredTimestamps();
  delete timestamps[projectId];
  localStorage.setItem(QA_TIMESTAMPS_KEY, JSON.stringify(timestamps));
};

// Pending starts: projects that have been kicked off to the Playwright service
// but haven't appeared in the database yet (link checking still in progress)
const getStoredPendingStarts = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(QA_PENDING_STARTS_KEY) || '[]');
    // Auto-clean entries older than PENDING_START_MAX_AGE_MS
    const now = Date.now();
    const valid = stored.filter(p => (now - p.startedAt) < PENDING_START_MAX_AGE_MS);
    if (valid.length !== stored.length) {
      localStorage.setItem(QA_PENDING_STARTS_KEY, JSON.stringify(valid));
    }
    return valid;
  } catch { return []; }
};

const addStoredPendingStart = (entry) => {
  const pending = getStoredPendingStarts();
  // Avoid duplicates by project name
  if (!pending.some(p => p.projectName === entry.projectName)) {
    pending.push(entry);
    localStorage.setItem(QA_PENDING_STARTS_KEY, JSON.stringify(pending));
  }
};

const removeStoredPendingStart = (projectName) => {
  const pending = getStoredPendingStarts();
  const updated = pending.filter(p => p.projectName !== projectName);
  localStorage.setItem(QA_PENDING_STARTS_KEY, JSON.stringify(updated));
};

export const QAProcessingProvider = ({ children }) => {
  const [activeProcesses, setActiveProcesses] = useState([]);
  const [pendingStarts, setPendingStarts] = useState(() => getStoredPendingStarts());
  const [initialized, setInitialized] = useState(false);
  const subscriptionsRef = useRef({});
  const lastProgressRef = useRef({}); // { [projectId]: { completedPages, timestamp } }

  // Add a pending start entry (project sent to Playwright service but not yet in DB)
  const addPendingStart = useCallback((projectName, totalPages = 0) => {
    const entry = {
      id: `pending-${projectName}-${Date.now()}`,
      projectName,
      totalPages,
      startedAt: Date.now(),
    };
    addStoredPendingStart(entry);
    setPendingStarts(prev => {
      if (prev.some(p => p.projectName === projectName)) return prev;
      return [...prev, entry];
    });
  }, []);

  // Remove a pending start (called when the real project is detected in DB)
  const removePendingStart = useCallback((projectName) => {
    removeStoredPendingStart(projectName);
    setPendingStarts(prev => prev.filter(p => p.projectName !== projectName));
  }, []);

  // Update a pending start with live progress from the Playwright service
  const updatePendingProgress = useCallback((projectName, updates) => {
    setPendingStarts(prev => prev.map(p =>
      p.projectName === projectName ? { ...p, ...updates } : p
    ));
  }, []);

  const addProcess = useCallback((projectId, projectName, totalPages = 0) => {
    const startedAt = Date.now();
    setStoredTimestamp(projectId, startedAt);

    setActiveProcesses(prev => {
      if (prev.some(p => p.projectId === projectId)) {
        return prev.map(p =>
          p.projectId === projectId
            ? { ...p, status: 'processing', progress: 0, totalPages, startedAt }
            : p
        );
      }
      return [...prev, {
        id: `qa-${projectId}-${Date.now()}`,
        projectId,
        projectName,
        status: 'processing',
        progress: 0,
        totalPages,
        completedPages: 0,
        startedAt,
      }];
    });
  }, []);

  const updateProcess = useCallback((projectId, updates) => {
    setActiveProcesses(prev =>
      prev.map(p =>
        p.projectId === projectId
          ? { ...p, ...updates }
          : p
      )
    );
  }, []);

  const removeProcess = useCallback((projectId) => {
    if (subscriptionsRef.current[projectId]) {
      subscriptionsRef.current[projectId]();
      delete subscriptionsRef.current[projectId];
    }
    removeStoredTimestamp(projectId);
    setActiveProcesses(prev => prev.filter(p => p.projectId !== projectId));
  }, []);

  const completeProcess = useCallback((projectId) => {
    if (subscriptionsRef.current[projectId]) {
      subscriptionsRef.current[projectId]();
      delete subscriptionsRef.current[projectId];
    }
    setActiveProcesses(prev =>
      prev.map(p =>
        p.projectId === projectId
          ? { ...p, status: 'completed', progress: 100 }
          : p
      )
    );
    setTimeout(() => {
      removeStoredTimestamp(projectId);
      setActiveProcesses(prev => prev.filter(p => p.projectId !== projectId));
    }, AUTO_REMOVE_COMPLETED_MS);
  }, []);

  const getProcess = useCallback((projectId) => {
    return activeProcesses.find(p => p.projectId === projectId);
  }, [activeProcesses]);

  const subscribeToProcess = useCallback((projectId, totalPages) => {
    if (subscriptionsRef.current[projectId]) return;

    // Initialize progress tracking for stuck detection
    lastProgressRef.current[projectId] = { completedPages: 0, timestamp: Date.now() };

    const unsubscribe = realtimeService.subscribeToProjectStatus(
      projectId,
      (status) => {
        const completedPages = status.completedPages || 0;
        const failedPages = status.failedPages || 0;
        const progress = status.totalPages > 0
          ? Math.round((completedPages / status.totalPages) * 100)
          : 0;

        // Track progress changes for stuck detection
        const prev = lastProgressRef.current[projectId];
        if (prev && completedPages !== prev.completedPages) {
          lastProgressRef.current[projectId] = { completedPages, timestamp: Date.now() };
        }

        // Determine if processing is stuck (no progress for STUCK_PROCESSING_TIMEOUT_MS)
        const timeSinceLastProgress = prev ? (Date.now() - prev.timestamp) : 0;
        const isStuck = status.processingPages > 0
          && timeSinceLastProgress > STUCK_PROCESSING_TIMEOUT_MS
          && status.status !== 'completed'
          && progress < 100;

        // Check if all remaining pages are failed (n8n crashed, watchdog marked them)
        const allRemainingFailed = status.totalPages > 0
          && (completedPages + failedPages) >= status.totalPages
          && failedPages > 0
          && status.pendingPages === 0
          && status.processingPages === 0;

        let processStatus = 'processing';
        if (status.status === 'completed' || progress >= 100) {
          processStatus = 'completed';
        } else if (allRemainingFailed) {
          processStatus = 'stuck';
        } else if (isStuck) {
          processStatus = 'stuck';
        } else if (status.status === 'in_progress' || status.status === 'processing') {
          processStatus = 'processing';
        }

        setActiveProcesses(prev =>
          prev.map(p =>
            p.projectId === projectId
              ? {
                ...p,
                progress,
                completedPages,
                failedPages,
                totalPages: status.totalPages || totalPages,
                status: processStatus
              }
              : p
          )
        );

        if (status.status === 'completed' || progress >= 100) {
          delete lastProgressRef.current[projectId];
          if (subscriptionsRef.current[projectId]) {
            subscriptionsRef.current[projectId]();
            delete subscriptionsRef.current[projectId];
          }
          setTimeout(() => {
            setActiveProcesses(prev => prev.filter(p => p.projectId !== projectId));
          }, AUTO_REMOVE_COMPLETED_MS);
        }

        // Auto-stop polling for stuck projects (watchdog/retry will handle recovery server-side)
        if (allRemainingFailed) {
          delete lastProgressRef.current[projectId];
          if (subscriptionsRef.current[projectId]) {
            subscriptionsRef.current[projectId]();
            delete subscriptionsRef.current[projectId];
          }
        }
      },
      POLL_INTERVAL_FAST_MS
    );

    subscriptionsRef.current[projectId] = unsubscribe;
  }, []);

  useEffect(() => {
    const detectActiveProcesses = async () => {
      try {
        // Fetch projects and active page counts in parallel
        const [projects, activeCounts] = await Promise.all([
          projectService.getProjects(),
          qaService.getActiveProcessingCounts(),
        ]);

        // Use accurate page-level counts to detect truly active projects
        const processingProjects = projects.filter(project => {
          const counts = activeCounts[project.id];
          return counts && (counts.processingPages > 0 || counts.pendingPages > 0);
        });

        // Clean up pending starts for projects that now exist in the DB
        const currentPending = getStoredPendingStarts();
        if (currentPending.length > 0) {
          const projectNames = new Set(projects.map(p => p.name));
          currentPending.forEach(pending => {
            if (projectNames.has(pending.projectName)) {
              removePendingStart(pending.projectName);
            }
          });
        }

        if (processingProjects.length > 0) {
          const storedTimestamps = getStoredTimestamps();

          processingProjects.forEach(project => {
            const counts = activeCounts[project.id] || { processingPages: 0, pendingPages: 0 };
            const totalPages = project.totalPages || 0;
            const completedPages = totalPages - counts.processingPages - counts.pendingPages;
            const progress = totalPages > 0
              ? Math.round((completedPages / totalPages) * 100)
              : 0;
            // Use stored timestamp, but reset it if it's stale (older than MAX_TIMESTAMP_AGE_MS)
            let startedAt = storedTimestamps[project.id];
            const isStaleTimestamp = startedAt && (Date.now() - startedAt) > MAX_TIMESTAMP_AGE_MS;
            if (!startedAt || isStaleTimestamp) {
              startedAt = Date.now();
              setStoredTimestamp(project.id, startedAt);
              if (isStaleTimestamp) {
                console.info(`[QAProcessing] Reset stale timestamp for project ${project.id} (was ${Math.round((Date.now() - storedTimestamps[project.id]) / 3600000)}h old)`);
              }
            }

            setActiveProcesses(prev => {
              if (prev.some(p => p.projectId === project.id)) return prev;
              return [...prev, {
                id: `qa-${project.id}-${Date.now()}`,
                projectId: project.id,
                projectName: project.name,
                status: 'processing',
                progress,
                totalPages,
                completedPages,
                startedAt,
              }];
            });

            subscribeToProcess(project.id, totalPages);
          });
        }
      } catch (error) {
        console.error('Failed to detect active processes:', error);
      } finally {
        setInitialized(true);
      }
    };

    detectActiveProcesses();

    return () => {
      Object.values(subscriptionsRef.current).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
      subscriptionsRef.current = {};
    };
  }, [subscribeToProcess]);

  // Poll Playwright service /job-status for each pending start to get real-time progress
  useEffect(() => {
    if (pendingStarts.length === 0) return;

    const pollJobStatus = async () => {
      for (const pending of pendingStarts) {
        try {
          const { data } = await axios.get(`${PLAYWRIGHT_SERVICE_URL}/job-status`, {
            params: { project_name: pending.projectName },
            timeout: 5000,
          });

          if (data.found) {
            updatePendingProgress(pending.projectName, {
              stage: data.stage,
              checkedPages: data.checkedPages || 0,
              totalPages: data.totalPages || pending.totalPages,
            });
          } else {
            // Job not found on server - either completed and forwarded to n8n,
            // or server was restarted/redeployed and job was lost.
            // Remove immediately — if the job truly just finished, detectActiveProcesses
            // will pick up the real project from the DB.
            console.info(`[QAProcessing] Job for "${pending.projectName}" not found on server. Cleaning up pending start.`);
            removePendingStart(pending.projectName);
          }
        } catch (err) {
          // Network error or service down (e.g. during redeployment).
          // If the pending start is older than 30s, clean it up so UI doesn't stay stuck.
          const age = Date.now() - pending.startedAt;
          if (age > 30 * 1000) {
            console.warn(`[QAProcessing] Service unreachable and pending start for "${pending.projectName}" is ${Math.round(age / 1000)}s old. Cleaning up.`);
            removePendingStart(pending.projectName);
          } else {
            console.warn(`[QAProcessing] Failed to poll job-status for "${pending.projectName}":`, err.message);
          }
        }
      }
    };

    // Poll immediately, then every POLL_INTERVAL_FAST_MS
    pollJobStatus();
    const interval = setInterval(pollJobStatus, POLL_INTERVAL_FAST_MS);
    return () => clearInterval(interval);
  }, [pendingStarts.length, updatePendingProgress, removePendingStart]);

  // Periodically re-run detectActiveProcesses to pick up projects that transition from pending to DB
  useEffect(() => {
    if (pendingStarts.length === 0) return;

    const checkForNewProjects = async () => {
      try {
        const projects = await projectService.getProjects();
        const projectNames = new Set(projects.map(p => p.name));
        const currentPending = getStoredPendingStarts();

        currentPending.forEach(pending => {
          if (projectNames.has(pending.projectName)) {
            // Project appeared in DB - find it and subscribe
            const project = projects.find(p => p.name === pending.projectName);
            if (project) {
              const completedPages = (project.passedPages || 0) + (project.failedPages || 0);
              const progress = project.totalPages > 0
                ? Math.round((completedPages / project.totalPages) * 100)
                : 0;
              const startedAt = pending.startedAt || Date.now();

              setActiveProcesses(prev => {
                if (prev.some(p => p.projectId === project.id)) return prev;
                return [...prev, {
                  id: `qa-${project.id}-${Date.now()}`,
                  projectId: project.id,
                  projectName: project.name,
                  status: 'processing',
                  progress,
                  totalPages: project.totalPages,
                  completedPages,
                  startedAt,
                }];
              });

              if (!subscriptionsRef.current[project.id]) {
                setStoredTimestamp(project.id, startedAt);
                subscribeToProcess(project.id, project.totalPages);
              }
            }
            removePendingStart(pending.projectName);
          }
        });
      } catch (err) {
        // Silently continue
      }
    };

    const interval = setInterval(checkForNewProjects, POLL_INTERVAL_FAST_MS);
    return () => clearInterval(interval);
  }, [pendingStarts.length, removePendingStart, subscribeToProcess]);

  const isAnyProcessing = activeProcesses.length > 0 || pendingStarts.length > 0;

  return (
    <QAProcessingContext.Provider value={{
      activeProcesses,
      pendingStarts,
      isAnyProcessing,
      addProcess,
      addPendingStart,
      removePendingStart,
      updateProcess,
      removeProcess,
      completeProcess,
      getProcess,
      subscribeToProcess,
      initialized,
    }}>
      {children}
    </QAProcessingContext.Provider>
  );
};

export const useQAProcessingContext = () => {
  const ctx = useContext(QAProcessingContext);
  if (!ctx) throw new Error('useQAProcessingContext must be used within QAProcessingProvider');
  return ctx;
};
